"use client";

import React, { useState, useEffect, useRef, Suspense, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment, Html, useGLTF } from "@react-three/drei";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Stack,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Container,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha, useTheme } from "@mui/material/styles";
import {
  CheckCircle,
  Assignment,
  VerifiedUser,
  ArrowForward,
  ErrorOutline,
  Fullscreen,
  Mic,
  Videocam,
  Wifi,
  CheckCircleOutline,
  HighlightOff,
  Assessment,
  Close,
  TrendingUp,
  Psychology,
  Code,
  Chat,
  Terminal,
} from "@mui/icons-material";
import axios from "axios";
import {
  initializeLipSync,
  speakWithLipSync,
  cancelSpeech,
  unlockAudio,
} from "./LipSyncService";
import Avatar from "./Avatar";
import QuestionPanel from "./QuestionPanel";
import apiClient from "../../context/apiClient";

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const examApiClient = axios.create({
  //baseURL: "http://127.0.0.1:8000/api",
  baseURL: "https://tdtlworld.com/dss-gatep-backend/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

examApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    if (
      ["POST", "PUT", "PATCH", "DELETE"].includes(config.method.toUpperCase())
    ) {
      const csrfToken = getCookie("csrftoken");
      if (csrfToken) config.headers["X-CSRFToken"] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

examApiClient.interceptors.response.use(
  (response) => {
    console.log("API Response:", response.status, response.data);
    return response;
  },
  (error) => {
    console.error("API Error:", error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

const examTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#D4AF37" }, // Gold
    secondary: { main: "#C0C0C0" }, // Silver
    background: {
      default: "#0A0A0A", // Charcoal/Black
      paper: "#121212",
    },
    text: { primary: "#FFFFFF", secondary: "#A9A5A5" },
    success: { main: "#2E7D32" },
    error: { main: "#D92626" },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: { textTransform: "none", fontWeight: 700 },
  },
  shape: { borderRadius: 24 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.error.main,
          color: theme.palette.common.white,
          borderRadius: "12px",
          fontWeight: 600,
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
        }),
      },
    },
  },
});

const avatarUrl = "https://models.readyplayer.me/686512bb52df6cb4f52b2fce.glb";
const idleAnimationUrl = `${process.env.PUBLIC_URL}/assets/male-idle-animation.glb`;
const talkingAnimationUrl = `${process.env.PUBLIC_URL}/assets/M_Talking_Variations_001.glb`;
const environmentHdrUrl = `${process.env.PUBLIC_URL}/assets/studio_small_03_1k.hdr`;

const MALPRACTICE_CAMERA_WARNING_LIMIT = 5;
const MALPRACTICE_TAB_SWITCH_LIMIT = 3;

const CanvasLoader = () => (
  <Html center>
    <CircularProgress sx={{ color: "#4F46BA" }} />
  </Html>
);

class ThreeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("3D Render Error:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Html center>
          <Box sx={{
            textAlign: 'center',
            p: 2,
            bgcolor: 'rgba(241, 255, 250, 0.95)',
            border: '1px solid #D92626',
            borderRadius: 2,
            minWidth: '200px'
          }}>
            <Typography variant="body2" color="error" fontWeight="bold">
              3D Avatar failed to load.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{ mt: 1, borderColor: '#4F46BA', color: '#4F46BA' }}
            >
              Retry
            </Button>
          </Box>
        </Html>
      );
    }
    return this.props.children;
  }
}

const Scene = ({ isSpeaking, animationQueueRef }) => {
  try {
    useGLTF.preload(avatarUrl);
    useGLTF.preload(idleAnimationUrl);
    useGLTF.preload(talkingAnimationUrl);
  } catch (e) {
    console.error("3D Preload error:", e);
  }
  return (
    <>
      <ambientLight intensity={0.7} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <ThreeErrorBoundary>
        <Avatar
          url={avatarUrl}
          idleAnimationUrl={idleAnimationUrl}
          talkingAnimationUrl={talkingAnimationUrl}
          isSpeaking={isSpeaking}
          animationQueueRef={animationQueueRef}
        />
      </ThreeErrorBoundary>
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 3}
      />
      <Environment files={environmentHdrUrl} />
    </>
  );
};

const CheckItem = ({ icon, title, status, delay = 0 }) => {
  const [visible, setVisible] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const getStatusIcon = () => {
    if (!visible) {
      return <CircularProgress size={20} sx={{ color: "#4F46BA" }} />;
    }
    if (status === "pending") {
      return <CircularProgress size={20} sx={{ color: "#4F46BA" }} />;
    }
    if (status === "granted") {
      return <CheckCircleOutline sx={{ color: "#2E7D32" }} />;
    }
    return <HighlightOff sx={{ color: "#D92626" }} />;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "24px",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: (theme) => `1px solid ${visible && status === "granted" ? alpha(theme.palette.primary.main, 0.4) : alpha(theme.palette.primary.main, 0.1)}`,
        opacity: visible ? 1 : 0.5,
        transition: "all 0.3s ease-in-out",
        boxShadow: visible && status === "granted" ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}` : 'none',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box sx={{ color: (theme) => visible && status === "granted" ? theme.palette.primary.main : theme.palette.text.secondary }}>
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 600, color: "text.primary" }}>
          {title}
        </Typography>
      </Stack>
      {getStatusIcon()}
    </Paper>
  );
};

const ReportDialog = ({ open, onClose, reportData }) => {
  const theme = useTheme();
  const getRoundIcon = (roundName) => {
    switch (roundName) {
      case "communication":
        return <Chat sx={{ color: "primary.main" }} />;
      case "psychometric":
        return <Psychology sx={{ color: "secondary.main" }} />;
      case "predict_output":
      case "fix_error":
      case "write_program":
        return <Code sx={{ color: "primary.main" }} />;
      default:
        return <Assessment />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "#2E7D32"; // Success
    if (score >= 6) return "#D4AF37"; // Gold/Warning
    return "#D92626"; // Error
  };

  const formatRoundName = (roundName) => {
    return roundName
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (!reportData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "28px",
          bgcolor: "#121212",
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      }}
    >
      <DialogTitle sx={{ p: 3, borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Assessment sx={{ color: "primary.main" }} />
            <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 700 }}>
              Interview Assessment Report
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "background.default", p: 3 }}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              borderRadius: "24px",
            }}
          >
            <CardContent>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <TrendingUp sx={{ fontSize: 40, color: "primary.main" }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 600 }}>
                        Global Readiness Score
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: "bold",
                          color: getScoreColor(reportData.score),
                        }}
                      >
                        {reportData.score}/10
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="body2"
                      gutterBottom
                      sx={{ color: "text.primary" }}
                    >
                      Performance Level
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={((reportData.score || 0) / 10) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: (theme) => alpha(theme.palette.common.white, 0.1),
                        "& .MuiLinearProgress-bar": {
                          bgcolor: getScoreColor(reportData.score),
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block", color: "#A9A5A5" }}
                    >
                      {reportData.score >= 8
                        ? "Excellent"
                        : reportData.score >= 6
                          ? "Good"
                          : reportData.score >= 4
                            ? "Average"
                            : "Needs Improvement"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {reportData.roundAnalysis && (
            <>
              <Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.1) }} />
              <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 600 }}>
                Round-wise Performance
              </Typography>
              <List>
                {Object.entries(reportData.roundAnalysis).map(
                  ([roundName, roundData]) => (
                    <ListItem
                      key={roundName}
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        mb: 1.5,
                        borderRadius: "16px",
                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        p: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ width: "100%" }}
                      >
                        {getRoundIcon(roundName)}
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle1"
                              sx={{
                                textTransform: "capitalize",
                                color: "text.primary",
                                fontWeight: 600,
                              }}
                            >
                              {formatRoundName(roundName)} Round
                            </Typography>
                          }
                          secondary={
                            <Typography sx={{ color: "text.secondary" }}>
                              {roundData.feedback ||
                                roundData.analysis ||
                                "Assessment completed successfully"}
                            </Typography>
                          }
                        />
                        <Chip
                          label={`${roundData.score || "N/A"}/10`}
                          sx={{
                            bgcolor: getScoreColor(roundData.score || 0),
                            color: "common.white",
                            fontWeight: 700,
                          }}
                          size="small"
                        />
                      </Stack>
                    </ListItem>
                  )
                )}
              </List>
            </>
          )}

          {reportData.languageAnalysis && (
            <>
              <Divider sx={{ borderColor: "#A9A5A5" }} />
              <Typography variant="h6" sx={{ color: "#000000" }}>
                Language Proficiency Analysis
              </Typography>
              <Card
                variant="outlined"
                sx={{ bgcolor: "#F1FFFA", borderColor: "#A9A5A5" }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                    <Typography variant="h6" sx={{ color: "#000000" }}>
                      Score: {reportData.languageProficiencyScore}/100
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={reportData.languageProficiencyScore}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#A9A5A5",
                        "& .MuiLinearProgress-bar": { bgcolor: "#4F46BA" },
                      }}
                    />
                  </Stack>
                  <Typography variant="body1" sx={{ color: "#000000" }}>
                    {reportData.languageAnalysis}
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}

          {reportData.candidateExperience && (
            <>
              <Divider sx={{ borderColor: "#A9A5A5" }} />
              <Typography variant="h6" sx={{ color: "#000000" }}>
                Experience Summary
              </Typography>
              <Card
                variant="outlined"
                sx={{ bgcolor: "#F1FFFA", borderColor: "#A9A5A5" }}
              >
                <CardContent>
                  <Typography variant="body1" sx={{ color: "#000000" }}>
                    {reportData.candidateExperience}
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}

          <Card
            variant="outlined"
            sx={{ bgcolor: "#F1FFFA", borderColor: "#A9A5A5" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: "#000000" }}>
                Summary
              </Typography>
              <Typography variant="body1" sx={{ color: "#000000" }}>
                {reportData.message ||
                  "Your interview assessment has been completed successfully. The detailed analysis above shows your performance across different rounds."}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ bgcolor: "background.default", p: 3, borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 4,
            borderRadius: "12px",
            fontWeight: 700,
          }}
        >
          Close Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExamInterface = () => {
  const hasReportedAbandonmentRef = useRef(false);
  const navigate = useNavigate();

  const [stage, setStage] = useState("LOADING_START");
  const [examData, setExamData] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const animationQueueRef = useRef([]);
  const currentAnswerRef = useRef("");

  const [isFullscreen, setIsFullscreen] = useState(
    !!document.fullscreenElement
  );
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const [permissionChecks, setPermissionChecks] = useState({
    network: "pending",
    camera: "pending",
    mic: "pending",
  });
  const [isChecking, setIsChecking] = useState(true);

  // --- CHANGE 1 START ---
  // Use a ref to hold the stream object. This prevents re-renders when the stream is created
  // and makes managing its lifecycle much more stable.
  const streamRef = useRef(null);
  // --- CHANGE 1 END ---

  const videoRef = useRef(null);

  const [malpracticeWarningCount, setMalpracticeWarningCount] = useState(0);

  const [userMicEnabled, setUserMicEnabled] = useState(false);
  const [answeringTimeRemaining, setAnsweringTimeRemaining] = useState(0);

  const answeringTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isTranscribingRef = useRef(false);
  const timerStateRef = useRef({ answering: 0, paused: false });

  const isSpeakingSessionRef = useRef(false);
  const speechEndTimerRef = useRef(null);

  const terminateInterview = useCallback(
    async (reason, type) => {
      if (stage === "ERROR") return;

      cancelSpeech();
      setStage("ERROR");
      try {
        const response = await examApiClient.post(
          "/mock-interview/malpractice/",
          { type_of_malpractice: type }
        );
        setError(
          `Interview terminated. Reason: ${response.data.malpractice_reason}`
        );
      } catch (err) {
        setError(
          `Interview terminated due to malpractice (${reason}). Failed to report to server.`
        );
      }
    },
    [stage]
  );

  const [isQuestionPanelTranscribing, setIsQuestionPanelTranscribing] =
    useState(false);

  const enterFullscreen = () => {
    document.documentElement.requestFullscreen().catch((err) => {
      console.error(
        `Error attempting to enable full-screen mode: ${err.message} (${err.name})`
      );
    });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const reportSessionAbandonment = useCallback(() => {
    if (stage !== "IN_PROGRESS") {
      return;
    }
    if (hasReportedAbandonmentRef.current) {
      return;
    }
    const token = localStorage.getItem("access_token");
    if (token) {
      const malpracticeApiUrl = `${examApiClient.defaults.baseURL}/mock-interview/malpractice/?auth_token=${token}`;
      const payload = { type_of_malpractice: "Session abandoned" };
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json; charset=UTF-8",
      });
      navigator.sendBeacon(malpracticeApiUrl, blob);
      hasReportedAbandonmentRef.current = true;
      console.log("Session abandonment report sent via Beacon.");
    } else {
      console.error(
        "Could not report session abandonment: auth token not found."
      );
    }
  }, [stage]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      reportSessionAbandonment();
      if (stage === "IN_PROGRESS") {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      reportSessionAbandonment();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [stage, reportSessionAbandonment]);

  useEffect(() => {
    const handleMalpractice = (type, message) => {
      if (stage !== "IN_PROGRESS") return;
      const newCount = tabSwitchCount + 1;
      setTabSwitchCount(newCount);
      if (newCount >= MALPRACTICE_TAB_SWITCH_LIMIT) {
        terminateInterview(message, "TAB_SWITCHING");
      } else {
        setWarningMessage(
          `Warning: ${message}. ${MALPRACTICE_TAB_SWITCH_LIMIT - newCount
          } warning(s) remaining.`
        );
        setShowWarning(true);
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden)
        handleMalpractice("Tab Switch", "Switching tabs is not allowed");
    };
    const handleFullscreenChangeForMalpractice = () => {
      if (!document.fullscreenElement)
        handleMalpractice(
          "Fullscreen Exit",
          "Exiting fullscreen is not allowed"
        );
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener(
      "fullscreenchange",
      handleFullscreenChangeForMalpractice
    );
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener(
        "fullscreenchange",
        handleFullscreenChangeForMalpractice
      );
    };
  }, [stage, tabSwitchCount, terminateInterview]);

  // Initial System Checks & Cleanup
  useEffect(() => {
    const initializeSystemAndInterview = async () => {
      setStage("LOADING_START");
      setIsChecking(true);
      const checks = { network: "denied", camera: "denied", mic: "denied" };
      try {
        await initializeLipSync();
        checks.network = "granted";
      } catch (e) {
        checks.network = "denied";
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: true });
        s.getTracks().forEach((t) => t.stop());
        checks.camera = "granted";
      } catch (e) {
        checks.camera = "denied";
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
        checks.mic = "granted";
      } catch (e) {
        checks.mic = "denied";
      }
      setPermissionChecks(checks);
      setIsChecking(false);
      setStage("NEEDS_VERIFICATION");
    };
    initializeSystemAndInterview();
    return () => {
      cancelSpeech();
      clearAllTimers();
    };
  }, []);

  // --- CHANGE 2 START ---
  // This new, single `useEffect` handles starting, attaching, and stopping the camera stream.
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const startStream = async () => {
      try {
        console.log("Requesting new camera stream...");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        streamRef.current = stream; // Store stream in the ref
        videoEl.srcObject = stream;
        videoEl.play().catch((e) => console.error("Error playing video:", e));
      } catch (err) {
        console.error("Failed to start camera stream:", err);
      }
    };

    const stopStream = () => {
      if (streamRef.current) {
        console.log("Stopping camera stream.");
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        videoEl.srcObject = null;
      }
    };

    const isCameraActiveStage = [
      "NEEDS_VERIFICATION",
      "VERIFYING",
      "IN_PROGRESS",
    ].includes(stage);

    if (isCameraActiveStage && permissionChecks.camera === "granted") {
      // If there's no stream, or the video element isn't displaying it, start/attach it.
      if (!streamRef.current || videoEl.srcObject !== streamRef.current) {
        startStream();
      }
    } else {
      // If we are not in an active stage, ensure the stream is stopped.
      stopStream();
    }

    // This is the final cleanup if the component unmounts unexpectedly.
    return () => {
      stopStream();
    };
  }, [stage, permissionChecks.camera, isFullscreen]); // `isFullscreen` re-triggers this hook to fix the black screen.
  // --- CHANGE 2 END ---

  // --- CHANGE 3 START ---
  // Malpractice check is now simpler and more robust.
  useEffect(() => {
    let malpracticeIntervalId;
    const captureAndSendMalpracticeCheck = async () => {
      // We only need to check for the stream in the ref.
      if (!videoRef.current || !streamRef.current || stage !== "IN_PROGRESS") {
        return;
      }

      // Check if the video is actually ready to be captured from
      if (videoRef.current.readyState < videoRef.current.HAVE_METADATA) {
        console.log("Video not ready for capture, skipping malpractice check.");
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas
        .getContext("2d")
        .drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const formData = new FormData();
        formData.append("captured", blob, "frame.jpeg");

        try {
          const { data } = await examApiClient.post(
            "/malpractice-check/",
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          if (data.multiple_faces) {
            await terminateInterview(
              "Multiple faces detected",
              "MULTIPLE_FACES"
            );
            return;
          }

          let isWarningTriggered = false;
          const warningMessages = [];
          let primaryTerminationReason = "";
          let primaryTerminationType = "";

          if (!data.match) {
            isWarningTriggered = true;
            warningMessages.push("Face not recognized");
            primaryTerminationReason = "Face not recognized";
            primaryTerminationType = "FACE_NOT_MATCHED";
          }
          if (!data.orientation_ok) {
            isWarningTriggered = true;
            warningMessages.push("Please look at the camera");
            if (!primaryTerminationReason) {
              primaryTerminationReason = "Not looking into camera";
              primaryTerminationType = "LOOKING_SIDEWAYS";
            }
          }
          if (data.detected_objects && data.detected_objects.length > 0) {
            isWarningTriggered = true;
            const objects = data.detected_objects.join(", ");
            warningMessages.push(`Prohibited object(s) detected: ${objects}`);
            if (!primaryTerminationReason) {
              primaryTerminationReason = `Prohibited object(s) detected: ${objects}`;
              primaryTerminationType = "PROHIBITED_OBJECT";
            }
          }

          if (isWarningTriggered) {
            const newCount = malpracticeWarningCount + 1;
            setMalpracticeWarningCount(newCount);

            if (newCount >= MALPRACTICE_CAMERA_WARNING_LIMIT) {
              await terminateInterview(
                primaryTerminationReason,
                primaryTerminationType
              );
            } else {
              const fullWarning = `Warning: ${warningMessages.join("; ")}. ${MALPRACTICE_CAMERA_WARNING_LIMIT - newCount
                } warning(s) remaining.`;
              setWarningMessage(fullWarning);
              setShowWarning(true);
            }
          }
        } catch (err) {
          console.error("Malpractice check API error:", err);
        }
      }, "image/jpeg");
    };

    if (stage === "IN_PROGRESS" && permissionChecks.camera === "granted") {
      malpracticeIntervalId = setInterval(
        captureAndSendMalpracticeCheck,
        15000
      );
    }
    return () => clearInterval(malpracticeIntervalId);
  }, [
    // Dependencies are simple now, as `streamRef` doesn't need to be listed.
    stage,
    permissionChecks.camera,
    malpracticeWarningCount,
    terminateInterview,
  ]);
  // --- CHANGE 3 END ---

  const clearAllTimers = () => {
    if (answeringTimerRef.current) clearTimeout(answeringTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
  };

  const handleAnswerChange = (newAnswer) => {
    currentAnswerRef.current = newAnswer;
  };

  // In ExamInterface.js

  // const startAnsweringTimer = () => {
  //   // --- ADD THIS CHECK AS THE VERY FIRST THING ---
  //   // Failsafe: If the speaking session is still marked as active, do not start the timer.
  //   if (isSpeakingSessionRef.current) {
  //     console.warn("Timer start blocked because a speaking session is active.");
  //     return;
  //   }

  //   console.log("Starting 1-minute answering timer. Mic enabled.");
  //   clearAllTimers();
  //   setAnsweringTimeRemaining(60);
  //   setUserMicEnabled(true);
  //   timerStateRef.current = { answering: 60, paused: false };

  //   countdownIntervalRef.current = setInterval(() => {
  //     if (!timerStateRef.current.paused) {
  //       timerStateRef.current.answering -= 1;
  //       setAnsweringTimeRemaining(timerStateRef.current.answering);
  //       if (timerStateRef.current.answering <= 0) {
  //         clearInterval(countdownIntervalRef.current);
  //         setUserMicEnabled(false);
  //         handleAutoSubmit();
  //       }
  //     }
  //   }, 1000);
  // };

  // In ExamInterface.js

  const startAnsweringTimer = () => {
    // Failsafe: Clear any lingering speech-end timers before starting.
    if (speechEndTimerRef.current) {
      clearTimeout(speechEndTimerRef.current);
    }

    console.log("Starting 1-minute answering timer. Mic enabled.");
    clearAllTimers();
    setAnsweringTimeRemaining(60);
    setUserMicEnabled(true);
    timerStateRef.current = { answering: 60, paused: false };

    countdownIntervalRef.current = setInterval(() => {
      if (!timerStateRef.current.paused) {
        timerStateRef.current.answering -= 1;
        setAnsweringTimeRemaining(timerStateRef.current.answering);
        if (timerStateRef.current.answering <= 0) {
          clearInterval(countdownIntervalRef.current);
          setUserMicEnabled(false);
          handleAutoSubmit();
        }
      }
    }, 1000);
  };

  const handleAutoSubmit = async () => {
    if (isSubmitting) return;
    const currentAnswer = String(currentAnswerRef.current || "");
    const answerText =
      currentAnswer.trim() || "Time expired - no answer provided";
    await handleSubmitAnswer(answerText);
  };

  // In ExamInterface.js

  // const handleQuestionSpeech = (data, isFirstQuestion = false) => {
  //   cancelSpeech();
  //   const speechParts = [];

  //   if (data.message) {
  //     speechParts.push(data.message);
  //   }

  //   const codeOnlyRounds = ["predict_output", "fix_error", "write_program"];

  //   if (!codeOnlyRounds.includes(data.current_round) && data.question_text) {
  //     speechParts.push(data.question_text);
  //   }

  //   const fullTextToSpeak = speechParts.join(" ");

  //   const speakAndProceed = () => {
  //     clearAllTimers();
  //     setAnsweringTimeRemaining(0);

  //     if (fullTextToSpeak.trim()) {
  //       // --- CHANGE A: Flip the master switch ON before speaking. ---
  //       isSpeakingSessionRef.current = true;

  //       speakWithLipSync(
  //         fullTextToSpeak,
  //         animationQueueRef,
  //         () => {
  //           setIsSpeaking(true);
  //           setUserMicEnabled(false);
  //         },
  //         () => {
  //           // --- CHANGE B: The speech is completely finished. ---
  //           setIsSpeaking(false);
  //           // Flip the master switch OFF.
  //           isSpeakingSessionRef.current = false;
  //           // NOW it is safe to start the timer.
  //           setTimeout(startAnsweringTimer, 1000);
  //         }
  //       );
  //     } else {
  //       // If there's nothing to say, ensure the switch is off and start the timer.
  //       isSpeakingSessionRef.current = false;
  //       setTimeout(startAnsweringTimer, 1000);
  //     }
  //   };

  //   setTimeout(speakAndProceed, isFirstQuestion ? 2000 : 1500);
  // };

  // In ExamInterface.js

  const handleQuestionSpeech = (data, isFirstQuestion = false) => {
    cancelSpeech();
    // Always clear any previous end-of-speech timers.
    if (speechEndTimerRef.current) {
      clearTimeout(speechEndTimerRef.current);
    }

    const speechParts = [];
    if (data.message) {
      speechParts.push(data.message);
    }
    const codeOnlyRounds = ["predict_output", "fix_error", "write_program"];
    if (!codeOnlyRounds.includes(data.current_round) && data.question_text) {
      speechParts.push(data.question_text);
    }
    const fullTextToSpeak = speechParts.join(" ");

    const speakAndProceed = () => {
      clearAllTimers();
      setAnsweringTimeRemaining(0);

      if (fullTextToSpeak.trim()) {
        // --- THIS IS THE NEW, RELIABLE LOGIC ---
        // 1. Set the application to "speaking" mode immediately.
        setIsSpeaking(true);
        setUserMicEnabled(false);

        // 2. Estimate the speech duration. A safe estimate is ~150 words per minute.
        //    (1 word = 400ms) + add a buffer of 1.5 seconds.
        const wordCount = fullTextToSpeak.split(" ").length;
        const estimatedDuration = wordCount * 400 + 1500; // in milliseconds

        console.log(
          `Estimated speech duration: ${estimatedDuration}ms for ${wordCount} words.`
        );

        // 3. Start the speech. We IGNORE the onEnd callback for timer logic.
        speakWithLipSync(
          fullTextToSpeak,
          animationQueueRef,
          () => {
            /* onStart does nothing new */
          },
          () => {
            // This onEnd ONLY sets isSpeaking to false for the avatar animation.
            // It no longer controls the timer.
            setIsSpeaking(false);
          }
        );

        // 4. Set our OWN reliable timer to end the "speaking" state and start the user's timer.
        speechEndTimerRef.current = setTimeout(() => {
          console.log("Reliable speech end timer fired. Starting user timer.");
          // We might have set isSpeaking to false a bit early from the onEnd,
          // but we ensure it's false now before starting the next phase.
          setIsSpeaking(false);
          startAnsweringTimer();
        }, estimatedDuration);
      } else {
        // If there's nothing to say, start the timer immediately.
        setIsSpeaking(false);
        startAnsweringTimer();
      }
    };

    setTimeout(speakAndProceed, isFirstQuestion ? 1000 : 500);
  };
  const handleVerifyIdentity = async () => {
    setStage("VERIFYING");
    try {
      await unlockAudio();
      enterFullscreen();

      const startResponse = await examApiClient.post("/mock-interview/start/");
      await examApiClient.post("/mock-interview/verify-identity/", {
        is_verified: true,
      });

      setExamData(startResponse.data);
      setStage("IN_PROGRESS");
      handleQuestionSpeech(startResponse.data, true);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Verification failed. Please try again."
      );
      setStage("ERROR");
    }
  };

  const handleSubmitAnswer = async (answerText) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    cancelSpeech();
    clearAllTimers();
    setAnsweringTimeRemaining(0);
    setUserMicEnabled(false);
    setIsSpeaking(false);

    try {
      const { data: newExamData } = await examApiClient.post(
        "/mock-interview/submit-answer/",
        { answer_text: answerText }
      );
      setExamData(newExamData);

      if (newExamData.status === "COMPLETED") {
        setStage("COMPLETED");
        if (document.fullscreenElement) document.exitFullscreen();

        setReportData({
          score: newExamData.global_readiness_score,
          message: newExamData.message,
          interviewId: newExamData.interview_id,
          reportUrl: newExamData.report_url,
        });

        try {
          console.log(
            "Interview completed. Triggering Skills Passport update..."
          );
          await apiClient.post("/skills-passport/", {});
          console.log("Successfully triggered Skills Passport update.");
        } catch (passportError) {
          console.error(
            "Failed to trigger Skills Passport update. This will not affect your interview results.",
            passportError
          );
        }

        setTimeout(() => {
          speakWithLipSync(
            "The interview is complete. Thank you. Your results have been generated.",
            animationQueueRef,
            () => setIsSpeaking(true),
            () => setIsSpeaking(false)
          );
        }, 1500);
      } else {
        currentAnswerRef.current = "";
        handleQuestionSpeech(newExamData, false);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Error submitting answer.");
      setStage("ERROR");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewReport = async () => {
    if (!reportData?.reportUrl) {
      const constructedUrl = `/mock-interview/report/${reportData.interviewId}/`;
      console.warn(
        "Report URL not available from completion API, using constructed URL:",
        constructedUrl
      );
      setReportData((prev) => ({ ...prev, reportUrl: constructedUrl }));
    }

    try {
      const urlToFetch =
        reportData.reportUrl ||
        `/mock-interview/report/${reportData.interviewId}/`;
      const { data: reportDetails } = await examApiClient.get(urlToFetch);

      setReportData((prev) => ({
        ...prev,
        roundAnalysis: reportDetails.round_analysis_json,
        languageAnalysis: reportDetails.language_analysis,
        languageProficiencyScore: reportDetails.language_proficiency_score,
        candidateExperience: reportDetails.candidate_experience,
      }));
      setShowReportDialog(true);
    } catch (err) {
      console.error("Failed to fetch detailed report:", err);
      setWarningMessage("Could not load the detailed report at this time.");
      setShowWarning(true);
    }
  };

  if (
    !isFullscreen &&
    ["NEEDS_VERIFICATION", "VERIFYING", "IN_PROGRESS"].includes(stage)
  ) {
    return (
      <ThemeProvider theme={examTheme}>
        <Container
          maxWidth="sm"
          sx={{ display: "flex", alignItems: "center", height: "100vh" }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: "28px",
              boxShadow: (theme) => `0 24px 64px ${alpha(theme.palette.common.black, 0.4)}`,
              textAlign: "center",
              width: "100%",
              bgcolor: "background.paper",
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <Fullscreen sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#000000" }}
            >
              Fullscreen Required
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "#A9A5A5" }}>
              For a secure and focused assessment, the interview must be
              conducted in fullscreen mode.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={enterFullscreen}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: "12px",
                fontWeight: 700,
              }}
            >
              Enter Fullscreen
            </Button>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  if (stage === "LOADING_START") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress sx={{ color: "primary.main" }} />
        <Typography variant="h6" sx={{ mt: 2, color: "text.primary", fontWeight: 600 }}>
          Initializing Interview & Checking System...
        </Typography>
      </Box>
    );
  }

  if (stage === "NEEDS_VERIFICATION" || stage === "VERIFYING") {
    const allChecksPassed =
      permissionChecks.network === "granted" &&
      permissionChecks.camera === "granted" &&
      permissionChecks.mic === "granted";
    return (
      <ThemeProvider theme={examTheme}>
        <Container
          maxWidth="md"
          sx={{ display: "flex", alignItems: "center", height: "100vh" }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: "28px",
              boxShadow: (theme) => `0 24px 64px ${alpha(theme.palette.common.black, 0.4)}`,
              textAlign: "center",
              width: "100%",
              bgcolor: "background.paper",
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <VerifiedUser sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              System & Identity Check
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: "#A9A5A5" }}>
              Please ensure your system is ready and verify your identity to
              begin the assessment.
            </Typography>
            <Stack spacing={2} sx={{ mb: 4, textAlign: "left" }}>
              <CheckItem
                icon={<Wifi />}
                title="Network Connection"
                status={permissionChecks.network}
                delay={0}
              />
              <CheckItem
                icon={<Videocam />}
                title="Camera Access"
                status={permissionChecks.camera}
                delay={1000}
              />
              <CheckItem
                icon={<Mic />}
                title="Microphone Access"
                status={permissionChecks.mic}
                delay={2000}
              />
            </Stack>
            {!isChecking && !allChecksPassed && (
              <Alert
                severity="error"
                sx={{
                  mb: 4,
                  bgcolor: "#F1FFFA",
                  color: "#D92626",
                  "& .MuiAlert-icon": { color: "#D92626" },
                }}
              >
                One or more system checks failed. Please grant the required
                permissions and ensure you have a stable network connection,
                then refresh the page.
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={handleVerifyIdentity}
              disabled={stage === "VERIFYING" || isChecking || !allChecksPassed}
              endIcon={
                stage === "VERIFYING" ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <ArrowForward />
                )
              }
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: "12px",
                fontWeight: 700,
              }}
            >
              {stage === "VERIFYING"
                ? "Verifying..."
                : "Verify & Proceed to Exam"}
            </Button>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  if (stage === "ERROR") {
    if (document.fullscreenElement) document.exitFullscreen();
    return (
      <ThemeProvider theme={examTheme}>
        <Container
          maxWidth="sm"
          sx={{ display: "flex", alignItems: "center", height: "100vh" }}
        >
          <Paper
            sx={{
              p: 5,
              borderRadius: 3,
              boxShadow: 3,
              textAlign: "center",
              width: "100%",
              bgcolor: "#F1FFFA",
            }}
          >
            <ErrorOutline sx={{ fontSize: 60, color: "#D92626", mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#000000" }}
            >
              An Error Occurred
            </Typography>
            <Alert
              severity="error"
              sx={{
                mb: 4,
                bgcolor: "#F1FFFA",
                color: "#D92626",
                "& .MuiAlert-icon": { color: "#D92626" },
              }}
            >
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate("/gatep/talent")}
              sx={{
                bgcolor: "#4F46BA",
                color: "#F1FFFA",
                "&:hover": { bgcolor: "#3730A3" },
              }}
            >
              Back to Dashboard
            </Button>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  if (stage === "IN_PROGRESS" || stage === "COMPLETED") {
    const formatTime = (seconds) =>
      `${Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
    return (
      <ThemeProvider theme={examTheme}>
        <Snackbar
          open={showWarning}
          autoHideDuration={4000}
          onClose={() => setShowWarning(false)}
          message={warningMessage}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        />
        <Box
          sx={{
            height: "100vh",
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AppBar
            position="static"
            elevation={0}
            sx={{
              bgcolor: "background.paper",
              borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
            }}
          >
            <Toolbar>
              <Assignment sx={{ mr: 2, color: "primary.main" }} />
              <Typography
                variant="h6"
                sx={{ flexGrow: 1, fontWeight: 700, color: "text.primary" }}
              >
                AI Assessment -{" "}
                <span style={{ textTransform: "capitalize" }}>
                  {(examData?.current_round || "Completed").replace(/_/g, " ")}
                </span>
              </Typography>
              <Chip
                label={`Question ${examData?.question_number}`}
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  fontWeight: 700,
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
                size="small"
              />
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "row",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: { xs: "40%", md: "35%" },
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                borderRight: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Box sx={{ flex: 1, position: "relative", minHeight: "60%" }}>
                <Canvas camera={{ position: [0, 0, 8], fov: 45 }}>
                  <Suspense fallback={<CanvasLoader />}>
                    <Scene
                      isSpeaking={isSpeaking}
                      animationQueueRef={animationQueueRef}
                    />
                  </Suspense>
                </Canvas>
                <Paper
                  elevation={0}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    right: 16,
                    p: 2,
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                    backdropFilter: "blur(12px)",
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: "16px",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: isSpeaking
                          ? "success.main"
                          : userMicEnabled
                            ? "primary.main"
                            : "text.disabled",
                        animation: isSpeaking ? "pulse 1.5s infinite" : "none",
                        "@keyframes pulse": {
                          "0%": { transform: "scale(1)" },
                          "50%": { transform: "scale(1.2)" },
                          "100%": { transform: "scale(1)" },
                        },
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    >
                      {isSpeaking
                        ? "AI Interviewer is speaking..."
                        : answeringTimeRemaining > 0
                          ? `Answering time: ${formatTime(
                            answeringTimeRemaining
                          )}${isQuestionPanelTranscribing ? " (Paused)" : ""}`
                          : "Please provide your answer."}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
              <Box
                sx={{
                  height: "40%",
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    borderRadius: 0,
                    p: 2,
                    bgcolor: "background.default",
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      bgcolor: "#000000",
                      position: "relative",
                      width: "100%",
                      "&::before": {
                        content: '""',
                        display: "block",
                        paddingTop: "66.67%",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                      }}
                    >
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transform: "scaleX(-1)",
                        }}
                      />
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -40%) scale(2)",
                          width: "90%",
                          height: "90%",
                          objectFit: "contain",
                          opacity: 0.3,
                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                      >
                        <source
                          src={`${process.env.PUBLIC_URL}/assets/scanner.mp4`}
                          type="video/mp4"
                        />
                      </video>
                      {/* --- CHANGE 4 START --- */}
                      {/* This overlay logic is simplified to just show when the stream isn't active */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#F1FFFA",
                          bgcolor: "rgba(0,0,0,0.7)",
                          zIndex: 1,
                          // Hide this overlay if the stream is active in the ref
                          opacity: streamRef.current ? 0 : 1,
                          pointerEvents: streamRef.current ? "none" : "auto",
                          transition: "opacity 0.5s",
                        }}
                      >
                        <Typography variant="caption" textAlign="center">
                          Camera not available
                        </Typography>
                      </Box>
                      {/* --- CHANGE 4 END --- */}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", position: "relative" }}>
              <QuestionPanel
                key={examData?.question_number}
                questionText={examData?.question_text}
                roundName={examData?.current_round}
                questionNumber={examData?.question_number}
                onSubmit={handleSubmitAnswer}
                isSubmitting={isSubmitting}
                userMicEnabled={userMicEnabled}
                onTranscribingChange={setIsQuestionPanelTranscribing}
                onAnswerChange={handleAnswerChange}
                answeringTimeRemaining={answeringTimeRemaining}
                isQuestionPanelTranscribing={isQuestionPanelTranscribing}
                isSpeaking={isSpeaking}
              />
            </Box>
          </Box>

          <Dialog
            open={stage === "COMPLETED"}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "28px",
                bgcolor: "background.paper",
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }
            }}
          >
            <DialogTitle
              sx={{ textAlign: "center", pt: 4, bgcolor: "transparent" }}
            >
              <CheckCircle sx={{ fontSize: 60, mb: 2, color: "success.main" }} />
              <Typography
                variant="h4"
                component="div"
                sx={{ color: "text.primary", fontWeight: 700 }}
              >
                Assessment Completed
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: "transparent" }}>
              <Stack spacing={3} sx={{ mt: 2 }}>
                <Typography sx={{ textAlign: "center", color: "#A9A5A5" }}>
                  {reportData?.message ||
                    "Thank you for completing the AI Interview Assessment."}
                </Typography>
                {reportData && (
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderRadius: "24px",
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Assessment
                              sx={{ color: "primary.main", fontSize: 40 }}
                            />
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{ color: "text.primary", fontWeight: 600 }}
                              >
                                Global Readiness Score
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{ fontWeight: "bold", color: "primary.main" }}
                              >
                                {reportData.score}/100
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={handleViewReport}
                            startIcon={<Assessment />}
                            sx={{
                              borderRadius: "12px",
                              fontWeight: 700,
                            }}
                          >
                            View Detailed Report
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </DialogContent>
            <DialogActions
              sx={{ justifyContent: "center", pb: 4, bgcolor: "transparent" }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/db-shiksha/gatep/talent")}
                sx={{
                  borderRadius: "12px",
                  fontWeight: 700,
                }}
              >
                Return to Dashboard
              </Button>
              {reportData && (
                <Button
                  variant="contained"
                  onClick={() =>
                    navigate(`/db-shiksha/gatep/talent/report/${reportData.interviewId}`)
                  }
                  startIcon={<Assessment />}
                  sx={{
                    borderRadius: "12px",
                    fontWeight: 700,
                  }}
                >
                  Go to Report Page
                </Button>
              )}
            </DialogActions>
          </Dialog>

          <ReportDialog
            open={showReportDialog}
            onClose={() => setShowReportDialog(false)}
            reportData={reportData}
          />
        </Box>
      </ThemeProvider>
    );
  }

  return null;
};

export default ExamInterface;
